import React, { useEffect, useMemo, useRef, useState } from "react";
import { noop } from "rxjs";
import cx from "classnames";
import { Node as ProseMirrorNode } from "prosemirror-model";
import { EditorState, TextSelection } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { generateNodeViews, generatePlugins, generateSchema, initializeView, } from "../../core/extension";
import { PortalContainer } from "../portals/portal-container";
import { EditorPortals } from "../portals/EditorPortals";
import { isDocEmpty } from "../../core/empty";
import { useWatchToolbarMode } from "../../extensions/formatting-toolbar/hooks";
/**
 * A base react wrapper for our ProseMirror editor.
 */
export const Editor = ({ extensions, editable = true, initialDoc = null, autoFocus = false, className, onStateChange = noop, onContentChange = noop, onViewCreated = noop, onBlur = noop, onFocus = noop, toolBarMode = null, children, }) => {
    const wrapper = useRef();
    const portalContainer = useMemo(() => new PortalContainer(), []);
    const schema = useMemo(() => generateSchema(extensions), [extensions]);
    const plugins = useMemo(() => generatePlugins(schema, extensions, portalContainer), [schema, extensions, portalContainer]);
    const nodeViews = useMemo(() => generateNodeViews(schema, extensions, portalContainer), [schema, extensions, portalContainer]);
    const [view, setView] = useState();
    useEffect(() => {
        const editorView = new EditorView(wrapper.current, {
            state: EditorState.create({
                doc: isDocEmpty(initialDoc)
                    ? null
                    : ProseMirrorNode.fromJSON(schema, initialDoc),
                schema,
                plugins,
            }),
            nodeViews,
            editable: () => editable,
            dispatchTransaction: (tr) => {
                const updatedState = editorView.state.apply(tr);
                editorView.updateState(updatedState);
                onStateChange(updatedState, tr.docChanged);
                if (tr.docChanged) {
                    onContentChange(updatedState.doc.toJSON());
                }
            },
            handleDOMEvents: {
                blur: (_, event) => {
                    onBlur(event);
                    return false;
                },
                focus: (_, event) => {
                    onFocus(event);
                    return false;
                },
            },
        });
        initializeView(editorView, extensions);
        setView(editorView);
        onViewCreated(editorView);
        return () => editorView.destroy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (view) {
            view.setProps({ editable: () => editable });
        }
    }, [view, editable]);
    useEffect(() => {
        if (view && autoFocus) {
            view.focus();
            view.dispatch(view.state.tr.setSelection(TextSelection.atEnd(view.state.doc)));
        }
    }, [view, autoFocus]);
    useWatchToolbarMode(view, toolBarMode, extensions, portalContainer);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: cx("editor", className), ref: wrapper, "data-testid": "editor" }),
        children,
        React.createElement(EditorPortals, { portalContainer: portalContainer })));
};
